import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // Amber-tinted radial wash instead of a flat black — clients see the
      // Photo Bridge brand behind every modal, even before they read a word.
      "fixed inset-0 z-50 bg-[radial-gradient(ellipse_at_center,rgba(120,53,15,0.55)_0%,rgba(0,0,0,0.88)_60%,rgba(0,0,0,0.94)_100%)] backdrop-blur-[2px]  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/** BrandBar — small logo + wordmark strip stamped at the top of every modal.
 * Kept as a separate export so callers who want a custom header can drop it
 * in explicitly, but by default DialogContent auto-injects it (see below). */
const DialogBrandBar = ({ className, ...props }) => (
  <div
    className={cn(
      "pb-branded-modal-bar -mx-6 -mt-6 mb-3 pl-6 pr-12 py-2.5 flex items-center justify-between gap-2 border-b border-amber-500/20 bg-gradient-to-r from-amber-500/10 via-transparent to-transparent",
      className
    )}
    {...props}
  >
    <div className="flex items-center gap-2">
      <img
        src="/logo-64.png"
        alt="Photo Bridge"
        width={20}
        height={20}
        className="object-contain shrink-0"
      />
      <span className="text-[10px] uppercase tracking-[0.18em] text-amber-400/90 font-semibold">Photo Bridge</span>
    </div>
    <span className="text-[10px] uppercase tracking-widest text-stone-500 hidden sm:inline">Studio delivered</span>
  </div>
)
DialogBrandBar.displayName = "DialogBrandBar"

const DialogContent = React.forwardRef(({ className, children, hideBrand = false, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Every modal now wears the Photo Bridge house palette by default:
        // stone-950 body, subtle amber ring, top gradient, rounded corners.
        // Any caller can still override background/border via className.
        "pb-branded-modal fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 bg-stone-950 border border-amber-500/15 ring-1 ring-amber-500/5 text-stone-100 p-6 shadow-[0_20px_60px_-15px_rgba(245,158,11,0.15)] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-lg overflow-hidden",
        className
      )}
      {...props}>
      {!hideBrand && <DialogBrandBar data-testid="pb-modal-brand-bar" />}
      {children}
      <DialogPrimitive.Close
        className="absolute right-3 top-2.5 rounded-sm opacity-70 text-stone-300 ring-offset-background transition-opacity hover:opacity-100 hover:text-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 disabled:pointer-events-none">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogBrandBar,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
